import clsx from 'clsx';
import { Button, LoadingSpinner, Stack } from 'components';
import { Drawer } from 'vaul';
import dayjs from 'dayjs';
import { useEffect, useRef, useCallback } from 'react';
import { getClient, getClientsList, putClientsFavourite } from 'store/clients/clientsActions';
import { getSites } from 'store/clientProfile/clientProfileActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useUserAuthStatusCheckerByPermission } from 'utils/jwtDecoder';
import { ROLE } from 'utils/protectedRoutes/userRolesAndPermissionTypes';
import { Link } from 'react-router-dom';
import {
  getClientsListSize,
  setClientsListSize,
  setClientsListScrollPosition,
  getClientsListScrollPosition,
  getLsUserRoleObject
} from 'modules/networkTools/localStorageTokens';
import debounce from 'lodash.debounce';
import { openAiScribeDrawer } from 'store/aiScribe/aiScribeSlice';
import {
  clearSearchText,
  initializeFilters,
  setChangeStatusModal,
  ClientsContentItem
} from 'store/clients/clientsSlice';

import { ActionMessage } from './ActionMessage';
import { Filters } from './filters';
import { SearchBar } from './searchBar';
import style from './mobileClients.module.scss';
import { getFiltersString } from './filters/filters';
import { ChangeStatusModal } from './clientForm/profileInfo/changeStatusModal';
import { getStatusSelectionNoteModalConfig, useNoteModal } from './noteModal/useNoteModal';

const MicrophoneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="inherit" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.9989 14.5C13.6589 14.5 14.9989 13.16 14.9989 11.5V5.5C14.9989 3.84 13.6589 2.5 11.9989 2.5C10.3389 2.5 8.99889 3.84 8.99889 5.5V11.5C8.99889 13.16 10.3389 14.5 11.9989 14.5ZM17.9089 11.5C17.4189 11.5 17.0089 11.86 16.9289 12.35C16.5189 14.7 14.4689 16.5 11.9989 16.5C9.52889 16.5 7.47889 14.7 7.06889 12.35C6.98889 11.86 6.57889 11.5 6.08889 11.5C5.47889 11.5 4.99889 12.04 5.08889 12.64C5.57889 15.64 7.97889 17.99 10.9989 18.42V20.5C10.9989 21.05 11.4489 21.5 11.9989 21.5C12.5489 21.5 12.9989 21.05 12.9989 20.5V18.42C16.0189 17.99 18.4189 15.64 18.9089 12.64C19.0089 12.04 18.5189 11.5 17.9089 11.5Z"
      fill="inherit"
    />
  </svg>
);

const formatListDate = (date: string) => dayjs(date).format('MM/DD/YY');

const getClientDetailsLine = (client: ClientsContentItem) => {
  const ageYears = client.dateOfBirth ? dayjs().diff(dayjs(client.dateOfBirth), 'year') : null;
  const agePart = ageYears !== null ? `${ageYears}` : '';
  const languagePart = client.primaryLanguage?.trim() ?? '';
  const phonePart = client.primaryPhoneNumber?.trim() ?? '';
  const mrnValue = `MRN/Client ID: ${client.clientIdentifier?.trim() || '--'}`;
  return (
    <p className={style.clientDetails}>
      {[agePart, languagePart, phonePart, mrnValue].filter(Boolean).join(', ')}
    </p>
  );
};

const getHospitalizationLine = (client: ClientsContentItem) => {
  if (!client.hospitalizationDate) return null;
  const dateStr = formatListDate(client.hospitalizationDate);
  const location = client.hospitalizationLocation?.trim();
  return (
    <p className={style.hospitalizationLine}>
      {location ? `Hospitalized: ${dateStr}, ${location}` : `Hospitalized: ${dateStr}`}
    </p>
  );
};

const List = ({
  scrollContainerRef,
  handleChangeStatusClick
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  handleChangeStatusClick: (clientId: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const { clientsList, statusesList } = useAppSelector((state) => state.clients);
  const { status, clientId } = useAppSelector((state) => state.aiScribe);
  const { permissions } = useUserAuthStatusCheckerByPermission();
  const canFavoriteAClient = permissions?.some((a: string) => a === ROLE.UNMARK_CLIENT_FOR_REVIEW);

  const setFavourite = (favourite: boolean, id: number) => {
    if (favourite && !canFavoriteAClient) return false;
    dispatch(putClientsFavourite({ id, isFavourite: !favourite }));
  };

  useEffect(() => {
    const scrollPosition = getClientsListScrollPosition();
    if (scrollPosition) {
      scrollContainerRef.current?.scrollTo({ top: scrollPosition });
    }
  }, []);

  return (
    <>
      {clientsList.content.map((client) => (
        <Stack key={client.id} borderBottom="1px solid #F1F5F9">
          <Stack
            display="flex"
            flexDirection="row"
            alignItems="center"
            py={client.aiScribeEnabled ? '12px' : '20px'}
            px="20px"
          >
            <Stack flex="1">
              <Button
                customStyleClass={style.favouriteButton}
                handleOnClick={() => setFavourite(client.favourite, client.id)}
                icon={
                  <span className={clsx('material-icons', client.favourite && style.favorite)}>
                    {client.favourite ? ' star' : 'star_border'}
                  </span>
                }
              />
            </Stack>
            <Stack flex="6" overflow="hidden">
              <Stack>
                <Link to={`/clients/edit/${client.id}`} className={style.clientName}>
                  {client.name}
                </Link>
              </Stack>
            </Stack>
            <Stack
              flex="3"
              display="flex"
              flexDirection="row"
              justifyContent={client.aiScribeEnabled ? 'flex-start' : 'flex-end'}
            >
              <Stack padding="0 8px" width="146px">
                <Button
                  theme="secondary"
                  size="small"
                  handleOnClick={() => handleChangeStatusClick(client.id.toString())}
                  text={statusesList.find((a) => a.id === client.status)?.name || ''}
                  customStyleClass={style.changeStatusButton}
                />
              </Stack>
            </Stack>
            {client.aiScribeEnabled && (
              <Stack flex="1" position="relative">
                <button
                  className={style.recordButton}
                  type="button"
                  disabled={status === 'recording' && clientId !== client.id}
                  onClick={() => {
                    if (status === 'recording' && clientId === client.id) return;
                    dispatch(
                      openAiScribeDrawer({
                        clientId: client.id,
                        clientName: client.name,
                        prependedClientId: client.prependedId.toString()
                      })
                    );
                  }}
                >
                  <MicrophoneIcon />
                </button>
                {status === 'recording' && Number(clientId) === client.id && (
                  <div className={style.redDot}></div>
                )}
              </Stack>
            )}
          </Stack>
          <Stack px="20px" pb="12px">
            <Stack display="flex" flexDirection="row" alignItems="center">
              <Stack flex="1"></Stack>
              <Stack flex={client.aiScribeEnabled ? '10' : '9'}>
                {getClientDetailsLine(client)}
              </Stack>
            </Stack>
            <Stack display="flex" flexDirection="row" alignItems="center">
              <Stack flex="1"></Stack>
              <Stack flex={client.aiScribeEnabled ? '10' : '9'}>
                {getHospitalizationLine(client)}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      ))}
    </>
  );
};

const MobileClientsPage = () => {
  const dispatch = useAppDispatch();
  const { filters, clientsList, clientsListLoading, changeStatusModal, searchText, statusesList } =
    useAppSelector((state) => state.clients);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isFirstLoadRef = useRef(true);
  const { handleOpen, noteModalElement } = useNoteModal();
  const { organizationId } = getLsUserRoleObject();

  const handleScrollToEnd = useCallback(() => {
    if (clientsList.content.length < clientsList.totalNumber && !clientsListLoading) {
      dispatch(
        getClientsList({
          page: 0,
          size: clientsList.size + 10,
          statuses: getFiltersString(filters),
          q: searchText
        })
      );
      setClientsListSize(clientsList.size + 10);
    }
  }, [filters, clientsList, dispatch, clientsListLoading, searchText]);

  const setScroll = debounce((scrollTop: number) => {
    setClientsListScrollPosition(scrollTop);
  }, 200);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

      // Store the current scroll position in local storage
      setScroll(scrollTop);

      // Check if user scrolled to the bottom (with small tolerance)
      if (scrollTop + clientHeight >= scrollHeight - 85) {
        handleScrollToEnd();
      }
    },
    [handleScrollToEnd]
  );

  useEffect(() => {
    dispatch(getSites());
    dispatch(initializeFilters());
  }, []);

  useEffect(() => {
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      return;
    }
    const size = getClientsListSize();
    dispatch(
      getClientsList({
        page: 0,
        size,
        statuses: getFiltersString(filters)
      })
    );
  }, [filters]);

  useEffect(() => {
    return () => {
      dispatch(clearSearchText());
    };
  }, []);

  const handleChangeStatusClick = async (clientId: string) => {
    await dispatch(getClient({ organizationId, clientId }));
    dispatch(setChangeStatusModal('changeStatus'));
  };

  const handleCloseModal = () => {
    dispatch(setChangeStatusModal(null));
  };

  const handleChangeStatusSuccess = (data?: any) => {
    const size = getClientsListSize();
    dispatch(
      getClientsList({
        page: 0,
        size,
        statuses: getFiltersString(filters)
      })
    );
    const noteModalConfig = getStatusSelectionNoteModalConfig(data, statusesList);
    if (noteModalConfig) handleOpen(noteModalConfig);
  };

  return (
    <Stack width="100%" className={style.wrapper}>
      <Stack width="100%" mt="8px" px="12px">
        <SearchBar />
      </Stack>
      <Stack display="flex" px="12px" flexDirection="row" width="100%" gap="8px" mt="8px">
        <Filters scrollContainerRef={scrollContainerRef} />
      </Stack>
      <Stack width="100%" mt="16px" px="20px">
        <ActionMessage />
      </Stack>
      <Stack
        display="flex"
        flexDirection="column"
        width="100%"
        mt="10px"
        pb="80px"
        height="100%"
        flexGrow={1}
        minHeight="0"
        overflow="auto"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        id="mobile-clients-list-scroll-container"
      >
        {clientsListLoading && clientsList.content.length === 0 ? (
          <Stack width="100%" height="100%" justifyContent="center" alignItems="center">
            <LoadingSpinner />
          </Stack>
        ) : (
          <List
            scrollContainerRef={scrollContainerRef}
            handleChangeStatusClick={handleChangeStatusClick}
          />
        )}
      </Stack>
      {changeStatusModal === 'changeStatus' && (
        <Drawer.Root
          open={changeStatusModal === 'changeStatus'}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) handleCloseModal();
          }}
          modal={false}
          autoFocus={false}
          dismissible={false}
        >
          <Drawer.Portal>
            <div className={style.changeStatusDrawerOverlay} />
            <Drawer.Content className={style.changeStatusDrawerContent}>
              <Stack position="absolute" right="12px" top="12px">
                <Button
                  icon={<span className="material-icons">close</span>}
                  type="button"
                  handleOnClick={handleCloseModal}
                  customStyleClass={style.closeButton}
                />
              </Stack>
              <div className={style.changeStatusDrawerBody}>
                <ChangeStatusModal
                  onClose={handleCloseModal}
                  onSubmitSuccess={handleChangeStatusSuccess}
                  isClientListPage={true}
                />
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      )}
      {noteModalElement}
    </Stack>
  );
};

export default MobileClientsPage;
